import { Injectable, inject } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { concatLatestFrom } from "@ngrx/operators";
import { Observable, catchError, filter, map, mergeMap, of, retry, switchMap, tap, throwError, timer } from "rxjs";
import { Action, Store } from "@ngrx/store";
import * as AppActions from "src/app/store/app/actions";
import * as UserActions from "src/app/store/user/actions";
import * as FromApp from "src/app/store/app/selectors";
import { AppRoutes, Languages } from "src/app/enums";
import localeBg from "@angular/common/locales/bg";
import localeCa from "@angular/common/locales/ca";
import localeEn from "@angular/common/locales/en";
import localeEs from "@angular/common/locales/es";
import localeDe from "@angular/common/locales/de";
import localeIt from "@angular/common/locales/it";
import localeFr from "@angular/common/locales/fr";
import localeHu from "@angular/common/locales/hu";
import { DOCUMENT, registerLocaleData } from "@angular/common";
import { TranslateService } from "@ngx-translate/core";
import { AdminService, CouponService, CustomerActionsService, Program, ProgramService } from "src/app/services/api";
import { HttpErrorResponse, HttpStatusCode } from "@angular/common/http";
import { Title } from "@angular/platform-browser";
import * as Sentry from "@sentry/capacitor";
import { toRgbChannels } from "src/app/utils/to-rgb-channels";

import { RootState } from "..";

@Injectable()
export class AppEffects {
  private readonly title = inject(Title);
  private readonly actions$ = inject(Actions);
  private readonly store = inject<Store<RootState>>(Store);
  private readonly adminService = inject(AdminService);
  private readonly programService = inject(ProgramService);
  private readonly couponService = inject(CouponService);
  private readonly customerActionsService = inject(CustomerActionsService);
  private readonly translateService = inject(TranslateService);
  private readonly document = inject<Document>(DOCUMENT);

  public changeSessionLanguage$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.changeSessionLanguage),
      mergeMap(action => {
        const previousLang = this.translateService.currentLang();
        const shouldReload = !action.silent && !!previousLang && previousLang !== action.language;

        switch (action.language) {
          case Languages.bg:
            registerLocaleData(localeBg);
            break;
          case Languages.ca:
            registerLocaleData(localeCa);
            break;
          case Languages.en:
            registerLocaleData(localeEn);
            break;
          case Languages.de:
            registerLocaleData(localeDe);
            break;
          case Languages.es:
            registerLocaleData(localeEs);
            break;
          case Languages.it:
            registerLocaleData(localeIt);
            break;
          case Languages.fr:
            registerLocaleData(localeFr);
            break;
          case Languages.hu:
            registerLocaleData(localeHu);
            break;
        }

        this.translateService.use(action.language);

        if (shouldReload) this.document.location.reload();

        return [AppActions.changeSessionLanguageSuccess({ language: action.language })];
      }),
    ),
  );

  public getProgram$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.getProgram),
      switchMap(({ slug, programId }) =>
        this.programService.programControllerGetProgramV1({ programId: slug || programId }).pipe(
          // A missing program is final and a 429 would only add load while rate-limited; 5xx and network errors get one more try.
          retry({
            count: 1,
            delay: (error: HttpErrorResponse) =>
              [HttpStatusCode.NotFound, HttpStatusCode.TooManyRequests].includes(error.status) ? throwError(() => error) : timer(1000),
          }),
          map(program => AppActions.getProgramSuccess({ program })),
          catchError((error: HttpErrorResponse) => of(AppActions.getProgramFailure({ error: error?.error?.message, status: error.status }))),
        ),
      ),
    ),
  );

  public getProgramSuccess$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppActions.getProgramSuccess),
        tap(({ program }) => {
          this.updateTitleAndFavicon(program);
          Sentry.setContext("jeri.program", {
            id: program.id,
            slug: program.slug,
          });
          const dark = getComputedStyle(this.document.documentElement).getPropertyValue("--ion-color-dark");
          const darkContrast = getComputedStyle(this.document.documentElement).getPropertyValue("--ion-color-dark-contrast");

          this.document.documentElement.style.setProperty(
            "--ion-color-program-background",
            program.configurations.web.backgroundColor || dark,
          );
          this.document.documentElement.style.setProperty(
            "--ion-color-program-foreground",
            program.configurations.web.foregroundColor || darkContrast,
          );
          this.updatePrimaryRgb(
            program.configurations.web.backgroundColor || dark,
            program.configurations.web.foregroundColor || darkContrast,
          );
        }),
      ),
    { dispatch: false },
  );

  public getProgramFailure$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppActions.getProgramFailure),
        // Only a program that does not exist is "not found"; other errors keep the visitor where they are.
        filter(({ status }) => status === HttpStatusCode.NotFound),
        tap(() => {
          this.document.querySelector(".--root-loading")?.classList.remove("--root-loading");
          this.document.location.href = AppRoutes.notFound;
        }),
      ),
    { dispatch: false },
  );

  public getProgramIntegrations$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.getProgramIntegrations),
      switchMap(params =>
        this.programService.programControllerGetProgramIntegrationsV1(params).pipe(
          map(programIntegrations => AppActions.getProgramIntegrationsSuccess({ programIntegrations })),
          catchError(() => of(AppActions.getProgramIntegrationsFailure())),
        ),
      ),
    ),
  );

  public getFeatures$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.getFeatures),
      switchMap(() =>
        this.adminService.adminControllerGetFeaturesV1().pipe(
          map(features => AppActions.getFeaturesSuccess({ features })),
          catchError(() => of(AppActions.getFeaturesFailure())),
        ),
      ),
    ),
  );

  public getCoupon$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.getCoupon),
      mergeMap(({ programId, couponId }) =>
        this.couponService.couponControllerGetCouponByIdV1({ programId, couponId }).pipe(
          map(coupon => AppActions.getCouponSuccess({ coupon })),
          catchError((error: HttpErrorResponse) => of(AppActions.getCouponFailure({ error: error?.error?.message }))),
        ),
      ),
    ),
  );

  public getEngagementFlowsWithProgramEngagementFlow$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.getEngagementFlowsWithProgramEngagementFlow),
      switchMap(({ programId, successCallback, errorCallback }) =>
        this.adminService.adminControllerGetEngagementFlowsWithProgramEngagementFlowV1({ programId }).pipe(
          map(engagementFlows => AppActions.getEngagementFlowsWithProgramEngagementFlowSuccess({ successCallback, engagementFlows })),
          catchError((error: HttpErrorResponse) =>
            of(AppActions.getEngagementFlowsWithProgramEngagementFlowFailure({ errorCallback, error: error?.error?.message })),
          ),
        ),
      ),
    ),
  );

  public getEngagementFlowsWithProgramEngagementFlowSuccess$: Observable<unknown> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppActions.getEngagementFlowsWithProgramEngagementFlowSuccess),
        filter(({ successCallback }) => !!successCallback),
        tap(({ successCallback, engagementFlows }) => successCallback(engagementFlows)),
      ),
    { dispatch: false },
  );

  public getEngagementFlowsWithProgramEngagementFlowFailure$: Observable<unknown> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppActions.getEngagementFlowsWithProgramEngagementFlowFailure),
        filter(({ errorCallback }) => !!errorCallback),
        tap(({ errorCallback, error }) => errorCallback(error)),
      ),
    { dispatch: false },
  );

  public previewWebConfig$: Observable<unknown> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppActions.previewWebConfig),
        concatLatestFrom(() => this.store.select(FromApp.selectProgram)),
        tap(([{ webConfig }, program]) => {
          const dark = getComputedStyle(this.document.documentElement).getPropertyValue("--ion-color-dark");
          const darkContrast = getComputedStyle(this.document.documentElement).getPropertyValue("--ion-color-dark-contrast");
          this.document.documentElement.style.setProperty(
            "--ion-color-program-background",
            program.configurations.web.backgroundColor || dark,
          );
          this.document.documentElement.style.setProperty(
            "--ion-color-program-foreground",
            program.configurations.web.foregroundColor || darkContrast,
          );
          this.updatePrimaryRgb(
            program.configurations.web.backgroundColor || dark,
            program.configurations.web.foregroundColor || darkContrast,
          );
          if (webConfig.logo) {
            this.updateFavicon(webConfig.logo);
          }
        }),
      ),
    { dispatch: false },
  );

  public getTiers$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.getTiers),
      mergeMap(({ programId }) =>
        this.adminService.adminControllerGetProgramTiersV1({ programId }).pipe(
          map(tiers => AppActions.getTiersSuccess({ tiers })),
          catchError(() => of(AppActions.getTiersFailure())),
        ),
      ),
    ),
  );

  // Boosts are time-windowed and customer-relevant, so fetch them alongside the customer data
  // (rather than at the cached CF edge) — every getCustomerData refreshes the active boost set.
  public getBoosts$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.getBoosts, UserActions.getCustomerData),
      concatLatestFrom(() => this.store.select(FromApp.selectProgramId)),
      filter(([, programId]) => !!programId),
      switchMap(([, programId]) =>
        this.programService.programControllerGetBoostsV1({ programId }).pipe(
          map(({ boosts }) => AppActions.getBoostsSuccess({ boosts })),
          catchError(() => of(AppActions.getBoostsFailure())),
        ),
      ),
    ),
  );

  public executeCustomEarningFlow$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.executeCustomEarningFlow),
      mergeMap(({ programId, engagementFlowName }) =>
        this.customerActionsService
          .customerActionsControllerExecuteCustomEarningFlowV1({
            programId,
            earningFlowId: engagementFlowName,
            executeCustomEarningFlowDto: {},
          })
          .pipe(
            map(response => AppActions.executeCustomEarningFlowSuccess({ engagementFlowName, response })),
            catchError((error: HttpErrorResponse) => of(AppActions.executeCustomEarningFlowFailure({ error: error?.error?.message }))),
          ),
      ),
    ),
  );

  private updateTitleAndFavicon({ name, slug, configurations: { web, wallet } }: Program): void {
    this.title.setTitle(name || slug);
    const logoUrl = web.logo || wallet.ios.icon || wallet.ios.storeCard.logo || wallet.android.programLogo;
    this.updateFavicon(logoUrl);
  }

  // Ionic builds alpha states (ripples, overlays) from the `-rgb` tokens, so keep them in sync with the program colors.
  private updatePrimaryRgb(background: string, foreground: string): void {
    const { style } = this.document.documentElement;
    const backgroundRgb = toRgbChannels(background);
    const foregroundRgb = toRgbChannels(foreground);

    if (backgroundRgb) style.setProperty("--ion-color-primary-rgb", backgroundRgb);
    else style.removeProperty("--ion-color-primary-rgb");
    if (foregroundRgb) style.setProperty("--ion-color-primary-contrast-rgb", foregroundRgb);
    else style.removeProperty("--ion-color-primary-contrast-rgb");
  }

  private updateFavicon(logoUrl: string): void {
    const faviconLink: HTMLLinkElement = this.document.querySelector('link[rel*="icon"]') || this.document.createElement("link");
    faviconLink.type = "image/png";
    faviconLink.rel = "icon";
    faviconLink.href = logoUrl;
    if (!this.document.querySelector('link[rel*="icon"]')) {
      this.document.head.appendChild(faviconLink);
    }
  }

  constructor() {}
}
